// Tournament page
// - Validates `:tournamentId` and `?edit` query locally
// - Redirects "new" without ?edit back to Admin list
// - Shows Spinner while Firebase data is loading
// - Renders <h1> error instead of using errorElement/loader

import { useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import TournamentCard from '../components/TournamentCard';
import Spinner from '../components/Spinner';
import { useTournament } from '../utils/hooks';

export default function PageTournament() {
  const { tournamentId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // One door for everyone, signed in or out: the entity registry reads this
  // tournament by id. 'new' is the create route (an admin's tournament, an
  // assigner's event), with no document behind it.
  const { entity: tournament, isResolved } = useTournament(
    tournamentId === 'new' ? null : tournamentId,
  );

  // Handle "create new" route: /tournaments/new
  useEffect(() => {
    // If user navigates to "new" without ?edit, send them back to Admin list
    if (tournamentId === 'new') {
      const editParam = searchParams.get('edit');
      if (!editParam) navigate(-1, { replace: true });
    }
  }, [tournamentId, searchParams, navigate]);

  // Case: creating a new tournament with ?edit present
  if (tournamentId === 'new') {
    const editParam = searchParams.get('edit');

    // If redirect just triggered (no ?edit), keep UI calm with a spinner
    if (!editParam) {
      return (
        <Layout screenSized className='page-tournament'>
          <Spinner show />
        </Layout>
      );
    }

    // Render creation screen; pass a minimal tournament shape if your card expects it
    return (
      <Layout screenSized className='page-tournament' trail>
        <TournamentCard tournament={{ id: 'new' }} />
      </Layout>
    );
  }

  // Spinner only while the record could still show up.
  if (!tournament && !isResolved) {
    return (
      <Layout screenSized className='page-tournament'>
        <Spinner show />
      </Layout>
    );
  }

  // Not found => plain H1 error (as requested)
  if (!tournament) {
    return (
      <Layout screenSized className='page-tournament'>
        <h1>{`Error: no route matches url "/tournaments/${tournamentId}"`}</h1>
      </Layout>
    );
  }

  // Happy path
  return (
    <Layout className='page-tournament' trail>
      <TournamentCard tournament={tournament} />
    </Layout>
  );
}
